// Maps a booking from the API (flat locations, vehicleOptions array, paymentStatus) onto
// the booking form's Formik values. Shared by the edit flow (EDIT_BOOKING) and the
// return trip sheet (RETURN_TRIP_BOOKING).

// The booking API returns flat locations, but the form expects a nested address object.
export const transformBookingLocation = (loc, isOriginWithFlightInfo = false) => {
  if (!loc) return null

  // Add FLIGHT_NBR tag to locationInformation if this is an airport (has flight info)
  const locationInformation = loc.locationInformation || []
  if (isOriginWithFlightInfo && !locationInformation.some(info => info.tag === 'FLIGHT_NBR')) {
    locationInformation.push({ tag: 'FLIGHT_NBR' })
  }

  return {
    ...loc,
    address: {
      streetName: loc.streetName,
      streetNumber: loc.streetNumber,
      city: loc.city,
      municipality: loc.municipality,
      zone: loc.zone,
      position: loc.position,
      region: loc.region,
      connectedRegions: loc.connectedRegions,
      locationType: loc.locationType,
      countryCode: loc.countryCode,
      poiName: loc.poiName
    },
    locationInformation
  }
}

// Service type and add-on form values from a booking's serviceType + vehicleOptions.
export const getVehicleOptionValues = booking => {
  const vehicleOptionsArray = booking.vehicleOptions || []
  // Handle estate as taxi + stationWagon
  const isEstate = booking.serviceType === 'taxi' && vehicleOptionsArray.includes('stationWagon')
  return {
    serviceType: isEstate ? 'estate' : booking.serviceType,
    pet: vehicleOptionsArray.includes('pet'),
    low: vehicleOptionsArray.includes('low'),
    // When serviceType is 'estate', stationWagon is already represented, so don't show it as separate option
    stationWagon: isEstate ? false : vehicleOptionsArray.includes('stationWagon'),
    rearFacingChildSeat: vehicleOptionsArray.includes('rearFacingChildSeat'),
    booster: vehicleOptionsArray.filter(opt => opt.startsWith('boosterSeat')).length,
    electric: vehicleOptionsArray.includes('electric'),
  }
}

// Payment radio value + account object for a booking. Accounts and cards that are no
// longer on the user's profile come back flagged `isOrphaned`.
export const resolvePaymentValues = (booking, user, paymentAccounts) => {
  let account = false
  let payment = 'DefaultNoSelect'
  if (booking.paymentStatus?.paymentMethod === 'CreditCard') {
    account = paymentAccounts?.find(acc => acc.accountId === booking.paymentStatus.accountNumber)
    if (account) {
      payment = `CreditCard-${account.accountId}`
    } else if (booking.paymentStatus.accountNumber) {
      // Handle credit card that's no longer in user's profile (orphaned)
      payment = `CreditCard-Orphaned-${booking.paymentStatus.accountNumber}`
      account = {
        accountId: booking.paymentStatus.accountNumber,
        maskedAccount: booking.paymentStatus.paymentMethodLabel || 'Credit Card',
        cardType: 'Unknown',
        type: 'creditcard',
        isOrphaned: true
      }
    }
  } else if (booking.paymentMethod === 'PayInCar') {
    payment = 'PayInCar'
  } else if (booking.paymentMethod === 'Klarna' || booking.paymentStatus?.paymentMethod === 'Klarna') {
    // Handle Klarna payments from other channels
    payment = 'Klarna'
  } else if (booking.paymentMethod === 'ApplePay' || booking.paymentStatus?.paymentMethod === 'ApplePay') {
    // Handle ApplePay payments from other channels
    payment = 'ApplePay'
  } else if (booking.account) {
    // Find the full account object from user's accounts (has flags and other properties)
    account = user?.accounts?.find(acc => acc.accountNumber === booking.account.number)
    if (account) {
      payment = `Account-${account.accountNumber}`
    } else {
      // Fallback to booking account if not found in user's accounts (orphaned)
      payment = `Account-Orphaned-${booking.account.number}`
      account = {
        accountNumber: booking.account.number,
        accountName: booking.account.name || 'Account',
        type: booking.account.type || 'TSAB',
        isOrphaned: true
      }
    }
  }
  return { payment, account }
}

// True when `payment` is something the web booking form can actually book with:
// a card or account that is still on the profile, or pay in car.
export const isBookablePayment = (payment, account) => {
  if (account?.isOrphaned) return false
  return payment === 'PayInCar' || /^(CreditCard|Account)-/.test(payment || '')
}
